import * as THREE from "three";
import { FIRE, pieceOf, toughness, type Grid } from "@dyson/shared";
import { DAMAGE_STAGES, damageStage } from "./cracks";

/** A flame: a teardrop, white-hot at the root, fading to orange at the tip. */
export const flameTexture = (() => {
  const c = document.createElement("canvas");
  c.width = 16;
  c.height = 24;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(8, 19, 1, 8, 15, 11);
  g.addColorStop(0, "rgba(255,240,200,1)");
  g.addColorStop(0.35, "rgba(255,170,60,0.9)");
  g.addColorStop(0.75, "rgba(220,70,20,0.5)");
  g.addColorStop(1, "rgba(120,20,0,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(8, 0);
  ctx.quadraticCurveTo(15, 12, 13, 19);
  ctx.quadraticCurveTo(8, 25, 3, 19);
  ctx.quadraticCurveTo(1, 12, 8, 0);
  ctx.fill();
  return new THREE.CanvasTexture(c);
})();

/** Rooted at the origin, rising up +Y. */
const flameGeometry = new THREE.PlaneGeometry(1.1, 1.6).translate(0, 0.65, 0);
/** A second, smaller tongue that licks up off the top of the flame. */
const tongueGeometry = new THREE.PlaneGeometry(0.6, 0.95).translate(0, 0.4, 0);
const emberGeometry = new THREE.PlaneGeometry(0.13, 0.13);
/** Brighter than white, so the bloom pass makes fires glow. */
export const flameMaterial = new THREE.MeshBasicMaterial({
  map: flameTexture,
  color: new THREE.Color(2.4, 1.5, 0.9),
  transparent: true,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
  fog: false,
});
const emberMaterial = new THREE.MeshBasicMaterial({
  color: new THREE.Color(3.2, 1.4, 0.4),
  transparent: true,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
  fog: false,
});
/** Sparks rising off each fire. */
const EMBERS = 3;

/**
 * One fire's flames, rooted at `group`'s origin and licking up its +Y (turn
 * the group to point them): a flickering teardrop, a smaller tongue that
 * sways up off its top and keeps breaking away, and a few rising embers. All
 * shared geometry and materials; animating it allocates nothing.
 */
export class Flame {
  readonly group = new THREE.Group();
  private readonly main = new THREE.Mesh(flameGeometry, flameMaterial);
  private readonly tongue = new THREE.Mesh(tongueGeometry, flameMaterial);
  private readonly embers: THREE.Mesh[] = [];

  /** `seed` keeps each fire's flicker out of step with the rest. */
  constructor(private readonly seed: number) {
    this.group.add(this.main, this.tongue);
    for (let k = 0; k < EMBERS; k++) {
      const ember = new THREE.Mesh(emberGeometry, emberMaterial);
      this.embers.push(ember);
      this.group.add(ember);
    }
  }

  /** Animate it at `time` (seconds); `grow` sizes it (about 1 for a fire well alight). */
  update(time: number, grow: number): void {
    const s = this.seed;
    const flicker = 0.85 + 0.15 * Math.sin(time * 17 + s * 1.7) + 0.08 * Math.sin(time * 41 + s);
    this.main.scale.set(grow * (0.9 + 0.1 * Math.sin(time * 13 + s)), grow * flicker, 1);
    // The tongue rises from inside the flame, swaying, and shrinks away as it goes; then another.
    const lick = (time * 2.3 + s * 0.37) % 1;
    const size = grow * (1 - lick) * (0.8 + 0.2 * Math.sin(time * 29 + s));
    this.tongue.position.set(grow * 0.18 * Math.sin(time * 7 + s * 2.3), grow * (0.35 + 0.6 * lick), 0.01);
    this.tongue.rotation.z = 0.25 * Math.sin(time * 5 + s);
    this.tongue.scale.set(size * 0.9, size, 1);
    // Embers drift up and wander, dimming (shrinking) to nothing.
    for (let k = 0; k < EMBERS; k++) {
      const p = (time * 0.7 + s * 0.13 + k / EMBERS) % 1;
      const ember = this.embers[k]!;
      ember.position.set(grow * 0.35 * Math.sin(p * 9 + s + k * 2), grow * (0.4 + 1.8 * p), 0.02);
      ember.scale.setScalar(1 - p);
    }
  }
}

/** One material per damage stage, shared by every ship. */
const damageMaterials = DAMAGE_STAGES.map((map) => new THREE.MeshBasicMaterial({ map, transparent: true, depthWrite: false }));

/** A `w` x `h` plane with the damage texture tiled once per block, so cracks on a piece aren't stretched. */
const damageGeometries = new Map<string, THREE.PlaneGeometry>();
function damageGeometry(w: number, h: number): THREE.PlaneGeometry {
  const key = `${w}x${h}`;
  let g = damageGeometries.get(key);
  if (!g) {
    g = new THREE.PlaneGeometry(w, h);
    const uv = g.getAttribute("uv") as THREE.BufferAttribute;
    for (let k = 0; k < uv.count; k++) uv.setXY(k, uv.getX(k) * w, uv.getY(k) * h);
    damageGeometries.set(key, g);
  }
  return g;
}

/**
 * Battle scars on a ship, in its grid's coordinates: cracks over blocks that
 * have taken hits (a piece's over all of it), worse the nearer they are to
 * breaking, and flames on burning ones.
 */
export class ShipScars {
  readonly group = new THREE.Group();
  private readonly cracks = new Map<number, THREE.Mesh>();
  private readonly flames = new Map<number, Flame>();
  private drawnDamage = "";

  update(grid: Grid, damage: Record<string, number> | undefined, fires: Record<string, number> | undefined, time: number): void {
    this.updateCracks(grid, damage);
    this.updateFlames(grid, fires ?? {}, time);
  }

  private updateCracks(grid: Grid, damage: Record<string, number> | undefined): void {
    const key = JSON.stringify(damage ?? {});
    if (key === this.drawnDamage) return;
    this.drawnDamage = key;
    // Geometry and materials are shared, so there's nothing to dispose.
    for (const mesh of this.cracks.values()) this.group.remove(mesh);
    this.cracks.clear();
    for (const [index, hits] of Object.entries(damage ?? {})) {
      const i = Number(index);
      const def = pieceOf(grid.tiles[i]!)?.def;
      const w = def?.w ?? 1;
      const h = def?.h ?? 1;
      const stage = damageStage(hits / toughness(grid.tiles[i]!));
      const mesh = new THREE.Mesh(damageGeometry(w, h), damageMaterials[stage]);
      mesh.position.set((i % grid.w) + w / 2, Math.floor(i / grid.w) + h / 2, 0.05);
      this.group.add(mesh);
      this.cracks.set(i, mesh);
    }
  }

  private updateFlames(grid: Grid, fires: Record<string, number>, time: number): void {
    for (const [i, flame] of this.flames) {
      if (fires[i] !== undefined) continue;
      this.group.remove(flame.group);
      this.flames.delete(i);
    }
    for (const [index, burned] of Object.entries(fires)) {
      const i = Number(index);
      let flame = this.flames.get(i);
      if (!flame) {
        // Rooted on the block's floor; "up" is the ship's own, as the group turns with it.
        flame = new Flame(i);
        flame.group.position.set((i % grid.w) + 0.5, Math.floor(i / grid.w), 0.4);
        this.group.add(flame.group);
        this.flames.set(i, flame);
      }
      // Fires grow as they burn.
      flame.update(time, 0.6 + 0.6 * Math.min(1, burned / FIRE.burnTime));
    }
  }
}
